import {
  addDoc,
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDocs,
  limit,
  onSnapshot,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import type { AccessListDoc, AccessListEntryType } from '@flintmint/core-shared/firestore/schemas';
import { getFirestore } from './firebase-service';

const COLLECTION_NAME = 'accessList';

export type AccessListEntry = AccessListDoc & { id: string };

export const subscribeToAccessList = (
  onData: (entries: AccessListEntry[]) => void,
  onError: (error: Error) => void
): (() => void) => {
  const firestore = getFirestore();
  return onSnapshot(
    collection(firestore, COLLECTION_NAME),
    (snapshot) => {
      onData(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...(docSnap.data() as AccessListDoc) })));
    },
    onError
  );
};

export const addAccessListEntry = async (type: AccessListEntryType, value: string, comment: string): Promise<void> => {
  const firestore = getFirestore();
  const data: AccessListDoc = { type, value, ...(comment && { comment }), updatedAt: new Date().toISOString() };
  await addDoc(collection(firestore, COLLECTION_NAME), data);
};

export const updateAccessListEntryComment = async (id: string, comment: string): Promise<void> => {
  const firestore = getFirestore();
  await updateDoc(doc(firestore, COLLECTION_NAME, id), {
    comment: comment || deleteField(),
    updatedAt: new Date().toISOString(),
  });
};

export const removeAccessListEntry = async (id: string): Promise<void> => {
  const firestore = getFirestore();
  await deleteDoc(doc(firestore, COLLECTION_NAME, id));
};

/**
 * admittedUserId -> email for every admitted email entry. First-contact admission stamps the user id on
 * the matched entry before `users` is written (at the token exchange, after the agreements), so this is
 * the only Firestore record of a pending user's email.
 */
export const fetchAdmittedEmailsByUserId = async (): Promise<Map<string, string>> => {
  const firestore = getFirestore();
  const snapshot = await getDocs(collection(firestore, COLLECTION_NAME));
  const emailsByUserId = new Map<string, string>();
  for (const docSnap of snapshot.docs) {
    const entry = docSnap.data() as AccessListDoc;
    if (entry.type === 'email' && entry.admittedUserId) {
      emailsByUserId.set(entry.admittedUserId, entry.value);
    }
  }
  return emailsByUserId;
};

/** The admitted email of one user (see `fetchAdmittedEmailsByUserId`), or null when no email entry admitted them. */
export const fetchAdmittedEmail = async (userId: string): Promise<string | null> => {
  const firestore = getFirestore();
  const snapshot = await getDocs(
    query(collection(firestore, COLLECTION_NAME), where('admittedUserId', '==', userId), where('type', '==', 'email'), limit(1))
  );
  const entry = snapshot.docs[0]?.data() as AccessListDoc | undefined;
  return entry?.value ?? null;
};
